import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppState,
  ActivityIndicator,
  Alert,
  BackHandler,
  Image,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Constants from 'expo-constants';
import { v4 as uuidv4 } from 'uuid';
import theme from '@/config/theme';
import ChecklistDateTimeField from '@/components/checklist/ChecklistDateTimeField';
import SignaturePad from '@/components/checklist/SignaturePad';
import InlineMarkPad, { type InlineMarkPadHandle } from '@/components/checklist/InlineMarkPad';
import PartnerPhotoField from '@/components/publicChecklistPartner/PartnerPhotoField';
import { groupChecklistRows } from '@/services/checklist/checklistLayout';
import { showToast } from '@/utils/toast';
import { prepareChecklistPhotoBlobWeb } from '@/utils/media/compressChecklistPhotoWeb';
import { MAX_PARTNER_MEDIA_BYTES } from '@/supabase/functions/_shared/partnerMedia';
import { generatePartnerReceiptPdf, getPartnerReceiptPdfErrorMessage } from '@/services/partnerChecklistReceiptPdf';
import {
  classifyPartnerDiagnosticCode,
  completePartnerChecklist,
  confirmPartnerMedia,
  getPartnerErrorMessage,
  getPartnerErrorProtocol,
  isPartnerConflict,
  isPartnerCompleted,
  isPartnerForbidden,
  isPartnerInactive,
  isPartnerRequiredPending,
  isPartnerUploadAlreadyExists,
  patchPartnerChecklist,
  preparePartnerMedia,
  resolvePartnerChecklist,
  resolvePartnerReceipt,
  submitPartnerChecklist,
  submitPartnerDiagnostic,
  uploadPartnerMedia,
  type PartnerChecklistField,
  type PartnerChecklistState,
  type PartnerChecklistReceipt,
  type PartnerMediaPreparation,
} from '@/services/partnerChecklistService';
import {
  createPartnerDraftKey,
  dataUrlToPartnerBlob,
  deletePartnerDraft,
  hasPartnerDraftWork,
  isPartnerDraftExpired,
  loadPartnerDraft,
  makeEmptyPartnerDraft,
  prunePartnerDraft,
  savePartnerDraft,
  type PartnerChecklistDraft,
  type PartnerDiagnosticPhase,
  type PartnerPendingDiagnostic,
  type PartnerPendingMedia,
} from '@/utils/partnerChecklistDraft';

type FailureNotice = { message: string; protocol: string | null; retry: () => void };
type PartnerSessionAction = 'submit' | 'complete';

const PARTNER_FOOTER = 'Informações técnicas sobre falhas podem ser registradas para suporte.';

const isPhotoField = (type: string) => /foto|photo|imagem/i.test(type);
const isSignatureField = (type: string) => /assinatura|signature/i.test(type);
const isMarkField = (type: string) => /marcação|marcacao|mark/i.test(type);
const isMediaField = (type: string) => isPhotoField(type) || isSignatureField(type) || isMarkField(type);
const isDateField = (type: string) => /timestamp|data|date/i.test(type);
const isDropdownField = (type: string) => type.toLowerCase().startsWith('dropdown');
const isMessageField = (type: string) => type.toLowerCase() === 'mensagem pronta';
const isCepField = (type: string) => type.toLowerCase() === 'cep1';
const isNumberField = (type: string) => /number|numeric|número|numero/i.test(type);

const buildLabel = (): string => {
  const version = Constants.expoConfig?.version ?? 'unknown';
  const build = Constants.expoConfig?.android?.versionCode;
  return build === undefined ? version : `${version}+${build}`;
};

const normalizeStringValue = (value: unknown): string | null =>
  typeof value === 'string' ? value : value == null ? null : String(value);

const valuesMatch = (left: string | null | undefined, right: string | null | undefined): boolean =>
  (left ?? null) === (right ?? null);

const orderedGroups = (fields: PartnerChecklistField[]) => {
  const groups = new Map<string, PartnerChecklistField[]>();
  for (const field of fields) {
    const name = field.grupo?.trim() || 'Checklist';
    const current = groups.get(name) ?? [];
    current.push(field);
    groups.set(name, current);
  }
  return [...groups.entries()].map(([title, groupFields]) => ({
    title,
    fields: groupFields.sort((a, b) => Number(a.ordem ?? 0) - Number(b.ordem ?? 0)),
  }));
};

const usableFields = (fields: PartnerChecklistField[]) => fields.filter((field) =>
  field.permission === 'read' || field.permission === 'edit'
);

const editableFieldIds = (fields: PartnerChecklistField[]) => new Set(
  fields.filter((field) => field.permission === 'edit' && field.editable !== false).map((field) => field.id)
);

const serverValueFor = (state: PartnerChecklistState, fieldId: string): string | null =>
  normalizeStringValue(state.values?.[fieldId]);

const serverObservationFor = (state: PartnerChecklistState, fieldId: string): string | null =>
  normalizeStringValue(state.observations?.[fieldId]);

const terminalPartnerState = (
  checklistStatus: 'in_progress' | 'done',
  revision: number,
  expiresAt: string
): PartnerChecklistState => ({
  mode: 'partner',
  revision,
  allow_complete: false,
  share_link: { id: 'completed', status: 'completed', expires_at: expiresAt },
  checklist: { status: checklistStatus },
  empresa: null,
  fields: [],
  values: {},
  observations: {},
  dropdown_options: {},
});

const valueFromDraftOrServer = (
  currentDraft: PartnerChecklistDraft | null,
  state: PartnerChecklistState,
  fieldId: string
): string | null => !state.receipt && currentDraft && Object.prototype.hasOwnProperty.call(currentDraft.values, fieldId)
  ? currentDraft.values[fieldId]
  : serverValueFor(state, fieldId);

const reconcileDraftWithState = (
  existing: PartnerChecklistDraft,
  state: PartnerChecklistState,
  key: string
): PartnerChecklistDraft => {
  const fields = usableFields(state.fields ?? []);
  const visibleIds = new Set(fields.map((field) => field.id));
  const editableIds = editableFieldIds(fields);
  const pruned = prunePartnerDraft({ ...existing, key, expiresAt: state.share_link.expires_at }, editableIds, visibleIds);
  const inFlight = pruned.patchInFlight;
  const inFlightEntries = inFlight ? [
    ...Object.entries(inFlight.changes).map(([fieldId, value]) => [fieldId, value, 'value'] as const),
    ...Object.entries(inFlight.observations).map(([fieldId, value]) => [fieldId, value, 'observation'] as const),
  ] : [];
  const inFlightApplied = Boolean(inFlight && inFlightEntries.length > 0 && inFlightEntries.every(([fieldId, value, kind]) =>
    valuesMatch(kind === 'value' ? serverValueFor(state, fieldId) : serverObservationFor(state, fieldId), value)
  ));
  const next: PartnerChecklistDraft = {
    ...pruned,
    patchInFlight: inFlightApplied ? null : inFlight,
    values: { ...pruned.values },
    observations: { ...pruned.observations },
  };

  for (const field of fields) {
    const hasLocalChange = Object.prototype.hasOwnProperty.call(next.pendingChanges, field.id);
    const hasInFlight = Boolean(next.patchInFlight && Object.prototype.hasOwnProperty.call(next.patchInFlight.changes, field.id));
    const hasLocalMedia = Object.prototype.hasOwnProperty.call(next.pendingMedia, field.id);
    if (!hasLocalChange && !hasInFlight && !hasLocalMedia) {
      next.values[field.id] = serverValueFor(state, field.id);
    }
    const hasLocalObservation = Object.prototype.hasOwnProperty.call(next.pendingObservations, field.id);
    const hasInFlightObservation = Boolean(next.patchInFlight && Object.prototype.hasOwnProperty.call(next.patchInFlight.observations, field.id));
    if (!hasLocalObservation && !hasInFlightObservation) {
      next.observations[field.id] = serverObservationFor(state, field.id);
    }
  }
  return next;
};

const getOnlineState = (): boolean => {
  if (Platform.OS === 'web' && typeof navigator !== 'undefined') return navigator.onLine;
  return true;
};

const PartnerChecklistPage = () => {
  const params = useLocalSearchParams<{ token?: string | string[] }>();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { width: windowWidth, fontScale } = useWindowDimensions();
  const columns = fontScale > 1.25 || windowWidth < 360 ? 1 : 2;
  const token = useMemo(() => (Array.isArray(params.token) ? params.token[0] : params.token) || '', [params.token]);
  const activeToken = useRef(token);
  activeToken.current = token;
  const [session, setSession] = useState<PartnerChecklistState | null>(null);
  const sessionRef = useRef<PartnerChecklistState | null>(null);
  const [draft, setDraft] = useState<PartnerChecklistDraft | null>(null);
  const draftRef = useRef<PartnerChecklistDraft | null>(null);
  const draftKeyRef = useRef<string | null>(null);
  const revisionRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const closingSession = useRef(false);
  const [closing, setClosing] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [successAction, setSuccessAction] = useState<PartnerSessionAction | 'consult' | null>(null);
  const [mediaBusy, setMediaBusy] = useState<Set<string>>(new Set());
  const preparingPhotos = useRef(new Set<string>());
  const [preparingPhotoCount, setPreparingPhotoCount] = useState(0);
  const [failure, setFailure] = useState<FailureNotice | null>(null);
  const [persistenceFailed, setPersistenceFailed] = useState(false);
  const [online, setOnline] = useState(getOnlineState());
  const onlineRef = useRef(online);
  const mountedRef = useRef(true);
  const operationQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const persistenceQueueRef = useRef<Promise<void>>(Promise.resolve());
  const diagnosticSendingRef = useRef(false);
  const [signatureField, setSignatureField] = useState<PartnerChecklistField | null>(null);
  const [markField, setMarkField] = useState<PartnerChecklistField | null>(null);
  const [mediaRebaseFieldId, setMediaRebaseFieldId] = useState<string | null>(null);
  const [expandedObservationField, setExpandedObservationField] = useState<string | null>(null);
  const [markPoints, setMarkPoints] = useState<Array<{ x: number; y: number }>>([]);
  const markPadRef = useRef<InlineMarkPadHandle>(null);
  const [requiredWarning, setRequiredWarning] = useState(false);
  const [conflictNeedsReview, setConflictNeedsReview] = useState(false);
  const retryTerminalRef = useRef<(action: PartnerSessionAction) => void>(() => undefined);
  const terminalNeedsRefreshRef = useRef(false);
  const retryMarkRef = useRef<() => void>(() => undefined);
  const retryMediaRef = useRef<(fieldId: string) => void>(() => undefined);

  const visibleFields = useMemo(() => usableFields(session?.fields ?? []), [session?.fields]);
  const groups = useMemo(() => orderedGroups(visibleFields), [visibleFields]);
  const editableIds = useMemo(() => editableFieldIds(visibleFields), [visibleFields]);
  const hasChecklistPending = Boolean(draft && (
    Object.keys(draft.pendingChanges).length > 0 ||
    Object.keys(draft.pendingObservations).length > 0 ||
    Object.keys(draft.pendingMedia).length > 0 ||
    draft.patchInFlight !== null ||
    draft.completionRequestId !== null
  ));

  const persistSnapshot = useCallback((next: PartnerChecklistDraft) => {
    persistenceQueueRef.current = persistenceQueueRef.current
      .catch(() => undefined)
      .then(() => savePartnerDraft(next))
      .then(() => { if (mountedRef.current) setPersistenceFailed(false); })
      .catch(() => { if (mountedRef.current) setPersistenceFailed(true); });
    return persistenceQueueRef.current;
  }, []);

  const replaceDraft = useCallback((next: PartnerChecklistDraft | null, persist = true) => {
    draftRef.current = next;
    if (mountedRef.current) setDraft(next);
    if (next && persist) void persistSnapshot(next);
  }, [persistSnapshot]);

  const updateSession = useCallback((next: PartnerChecklistState | null) => {
    sessionRef.current = next;
    revisionRef.current = next?.revision ?? revisionRef.current;
    if (mountedRef.current) setSession(next);
  }, []);

  const loadReceipt = useCallback(async (): Promise<PartnerChecklistReceipt> => {
    const receipt = await resolvePartnerReceipt(token);
    if (receipt.receipt !== true || receipt.share_link.status !== 'completed') throw new Error('Comprovante inválido.');
    updateSession(receipt);
    return receipt;
  }, [token, updateSession]);

  const downloadReceipt = useCallback(async () => {
    setPdfBusy(true);
    setFailure(null);
    let generating = false;
    try {
      // Revalida TTL/revogação e renova URLs de mídia antes de cada geração.
      const receipt = await loadReceipt();
      generating = true;
      await generatePartnerReceiptPdf(receipt);
    } catch (error) {
      setFailure({ message: getPartnerReceiptPdfErrorMessage(error) ?? (generating
        ? 'Não foi possível gerar o PDF. Tente novamente ou solicite o PDF à empresa.' : getPartnerErrorMessage(error)),
      protocol: getPartnerErrorProtocol(error), retry: () => { void downloadReceipt(); } });
    } finally { setPdfBusy(false); }
  }, [loadReceipt]);

  const enqueue = useCallback(<T,>(operation: () => Promise<T>): Promise<T> => {
    const next = operationQueueRef.current.then(operation, operation);
    operationQueueRef.current = next.catch(() => undefined);
    return next;
  }, []);

  const attemptPendingDiagnostic = useCallback(async () => {
    const current = draftRef.current;
    if (!token || !current?.diagnostic || diagnosticSendingRef.current) return;
    diagnosticSendingRef.current = true;
    try {
      const result = await submitPartnerDiagnostic(token, current.diagnostic);
      if (result === 'sent' || result === 'limited') {
        const latest = draftRef.current;
        if (latest?.diagnostic?.requestId === current.diagnostic.requestId &&
            latest.diagnostic.phase === current.diagnostic.phase) {
          replaceDraft({ ...latest, diagnostic: null });
        }
      }
    } finally {
      diagnosticSendingRef.current = false;
    }
  }, [replaceDraft, token]);

  const noteFailure = useCallback((
    error: unknown,
    phase: PartnerDiagnosticPhase,
    requestId: string,
    retry: () => void
  ) => {
    const protocol = getPartnerErrorProtocol(error) || requestId;
    setFailure({ message: getPartnerErrorMessage(error), protocol, retry });
    const key = draftRef.current?.key ?? draftKeyRef.current;
    if (key) {
      const current = draftRef.current ?? makeEmptyPartnerDraft(key, new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString());
      const existing = current.diagnostic;
      const diagnostic: PartnerPendingDiagnostic = existing ?? {
        requestId,
        phase,
        code: classifyPartnerDiagnosticCode(error),
        online: onlineRef.current,
        attempt: 1,
        build: buildLabel(),
      };
      const next = { ...current, diagnostic };
      replaceDraft(next);
      if (onlineRef.current) void attemptPendingDiagnostic();
    }
  }, [attemptPendingDiagnostic, replaceDraft]);

  const setOnlineState = useCallback((nextOnline: boolean) => {
    onlineRef.current = nextOnline;
    if (mountedRef.current) setOnline(nextOnline);
    if (nextOnline) void attemptPendingDiagnostic();
  }, [attemptPendingDiagnostic]);

  const mergeResolvedState = useCallback(async (fresh: PartnerChecklistState, key: string) => {
    const current = draftRef.current;
    const base = current ?? makeEmptyPartnerDraft(key, fresh.share_link.expires_at);
    const next = reconcileDraftWithState(base, fresh, key);
    if (fresh.share_link.status === 'expired' || fresh.share_link.status === 'revoked' || isPartnerDraftExpired(next)) {
      await deletePartnerDraft(key);
      replaceDraft(null, false);
    } else {
      replaceDraft(next);
    }
    updateSession(fresh);
    setOnlineState(true);
    setFailure(null);
    void attemptPendingDiagnostic();
    return next;
  }, [attemptPendingDiagnostic, replaceDraft, setOnlineState, updateSession]);

  const resolveAndMerge = useCallback(async (key: string) => {
    const fresh = await resolvePartnerChecklist(token);
    return mergeResolvedState(fresh, key);
  }, [mergeResolvedState, token]);

  const loadChecklist = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const requestId = uuidv4();
    let key = draftKeyRef.current;
    let pendingTerminalRequestId: string | null = null;
    let readingReceipt = false;
    let confirmedTerminal = false;
    try {
      key = await createPartnerDraftKey(token);
      draftKeyRef.current = key;
      const existing = await loadPartnerDraft(key);
      if (existing) replaceDraft(existing, false);

      if (existing?.completionRequestId) {
        pendingTerminalRequestId = existing.completionRequestId;
        const expectedRevision = existing.completionExpectedRevision;
        if (expectedRevision === null || !Number.isFinite(expectedRevision)) {
          throw new Error('O pedido de encerramento salvo está incompleto.');
        }
        const action = existing.completionMode ?? 'submit';
        const result = action === 'submit'
          ? await submitPartnerChecklist(token, existing.completionRequestId, expectedRevision)
          : await completePartnerChecklist(token, existing.completionRequestId, expectedRevision);
        if (result.session_status !== 'completed' ||
            (result.checklist_status !== 'in_progress' && result.checklist_status !== 'done')) {
          throw new Error('Resposta de encerramento inválida.');
        }
        const terminalState = terminalPartnerState(result.checklist_status, result.revision, existing.expiresAt);
        updateSession(terminalState);
        const latest = draftRef.current ?? existing;
        replaceDraft({
          ...latest,
          completionRequestId: null,
          completionExpectedRevision: null,
          completionMode: null,
          terminalChecklistStatus: result.checklist_status,
          terminalRevision: result.revision,
        });
        terminalNeedsRefreshRef.current = false;
        confirmedTerminal = true;
        setSuccessAction(result.checklist_status === 'done' ? 'complete' : 'submit');
        setOnlineState(true);
        setFailure(null);
        void attemptPendingDiagnostic();
        pendingTerminalRequestId = null;
        readingReceipt = true;
        const receipt = await loadReceipt();
        if (result.checklist_status !== 'done' && !receipt.fields.some(field => field.permission === 'edit')) setSuccessAction('consult');
        return;
      }

      if (existing?.terminalChecklistStatus && typeof existing.terminalRevision === 'number') {
        updateSession(terminalPartnerState(existing.terminalChecklistStatus, existing.terminalRevision, existing.expiresAt));
        confirmedTerminal = true;
        readingReceipt = true;
        await loadReceipt();
        setOnlineState(true);
        setFailure(null);
        return;
      }

      if (existing && isPartnerDraftExpired(existing)) {
        await deletePartnerDraft(key);
        replaceDraft(null, false);
      }
      await resolveAndMerge(key);
    } catch (error) {
      if (isPartnerCompleted(error)) {
        try {
          await loadReceipt(); setFailure(null); setOnlineState(true);
        } catch (receiptError) {
          updateSession(null);
          setFailure({ message: `Este acesso de edição já foi encerrado. ${getPartnerReceiptPdfErrorMessage(receiptError) ?? getPartnerErrorMessage(receiptError)}`, protocol: getPartnerErrorProtocol(receiptError), retry: () => { void loadChecklist(); } });
        }
        return;
      }
      if (readingReceipt) {
        if (!confirmedTerminal) updateSession(null);
        setFailure({ message: `${confirmedTerminal ? 'O encerramento foi confirmado. ' : ''}${getPartnerReceiptPdfErrorMessage(error) ?? getPartnerErrorMessage(error)}`, protocol: getPartnerErrorProtocol(error), retry: () => { void loadChecklist(); } });
        return;
      }
      setOnlineState(classifyPartnerDiagnosticCode(error) !== 'network_error');
      if (pendingTerminalRequestId && key && (isPartnerConflict(error) || isPartnerRequiredPending(error))) {
        const current = draftRef.current;
        if (current) replaceDraft({
          ...current, completionRequestId: null, completionExpectedRevision: null, completionMode: null,
        });
        try {
          await resolveAndMerge(key);
          setConflictNeedsReview(isPartnerConflict(error));
          setFailure({
            message: isPartnerRequiredPending(error) ? getPartnerErrorMessage(error)
              : 'O checklist foi atualizado. Confira os dados antes de enviar novamente.',
            protocol: getPartnerErrorProtocol(error) || pendingTerminalRequestId,
            retry: () => { void loadChecklist(); },
          });
        } catch (refreshError) {
          noteFailure(refreshError, 'resolve', requestId, () => { void loadChecklist(); });
        }
        return;
      }
      noteFailure(error, pendingTerminalRequestId ? 'complete' : 'resolve', pendingTerminalRequestId ?? requestId, () => { void loadChecklist(); });
      if (isPartnerInactive(error) && key) {
        await deletePartnerDraft(key).catch(() => undefined);
        replaceDraft(null, false);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [attemptPendingDiagnostic, completePartnerChecklist, loadReceipt, noteFailure, replaceDraft, resolveAndMerge, setOnlineState, submitPartnerChecklist, token, updateSession]);

  useEffect(() => {
    mountedRef.current = true;
    void loadChecklist();
    return () => {
      mountedRef.current = false;
    };
  }, [loadChecklist]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const onOnline = () => setOnlineState(true);
    const onOffline = () => setOnlineState(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [setOnlineState]);

  useEffect(() => {
    if (Platform.OS === 'web') return;
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active' && draftRef.current?.diagnostic) {
        setOnlineState(true);
        void attemptPendingDiagnostic();
      }
    });
    return () => subscription.remove();
  }, [attemptPendingDiagnostic, setOnlineState]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !hasChecklistPending || typeof window === 'undefined') return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasChecklistPending]);

  useEffect(() => {
    if (Platform.OS === 'web' || !hasChecklistPending) return;
    const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
      Alert.alert('Alterações pendentes', 'Há alterações que ainda não foram enviadas.', [
        { text: 'Continuar preenchendo', style: 'cancel' },
        { text: 'Sair', style: 'destructive', onPress: () => router.back() },
      ]);
      return true;
    });
    return () => subscription.remove();
  }, [hasChecklistPending, router]);

  useEffect(() => {
    if (!draft || !session || conflictNeedsReview || (
      !Object.keys(draft.pendingChanges).length && !Object.keys(draft.pendingObservations).length
    )) return;
    const timer = setTimeout(() => { void flushPendingChanges(); }, 900);
    return () => clearTimeout(timer);
  }, [conflictNeedsReview, draft?.updatedAt, session, draft?.pendingChanges]);

  const requeueAfterConflict = useCallback(async (key: string) => {
    const fresh = await resolvePartnerChecklist(token);
    const current = draftRef.current ?? makeEmptyPartnerDraft(key, fresh.share_link.expires_at);
    const inFlight = current.patchInFlight;
    const merged = reconcileDraftWithState(current, fresh, key);
    const pendingChanges = { ...merged.pendingChanges };
    const pendingObservations = { ...merged.pendingObservations };
    if (inFlight) {
      for (const [fieldId, value] of Object.entries(inFlight.changes)) {
        if (!valuesMatch(serverValueFor(fresh, fieldId), value)) pendingChanges[fieldId] = value;
      }
      for (const [fieldId, value] of Object.entries(inFlight.observations)) {
        if (!valuesMatch(serverObservationFor(fresh, fieldId), value)) pendingObservations[fieldId] = value;
      }
    }
    const next = { ...merged, patchInFlight: null, pendingChanges, pendingObservations };
    replaceDraft(next);
    updateSession(fresh);
    setOnlineState(true);
  }, [replaceDraft, setOnlineState, token, updateSession]);

  const flushPendingChanges = useCallback((): Promise<boolean> => enqueue(async () => {
    const key = draftKeyRef.current;
    if (!key || !sessionRef.current || !draftRef.current) return false;
    while (true) {
      let current = draftRef.current;
      if (!current) return false;
      if (!current.patchInFlight && Object.keys(current.pendingChanges).length === 0 && Object.keys(current.pendingObservations).length === 0) return true;
      if (!current.patchInFlight) {
        const changes = { ...current.pendingChanges };
        const observations = { ...current.pendingObservations };
        const inFlight = { requestId: uuidv4(), expectedRevision: revisionRef.current, changes, observations };
        const remaining = { ...current.pendingChanges };
        const remainingObservations = { ...current.pendingObservations };
        Object.keys(changes).forEach((fieldId) => delete remaining[fieldId]);
        Object.keys(observations).forEach((fieldId) => delete remainingObservations[fieldId]);
        current = { ...current, pendingChanges: remaining, pendingObservations: remainingObservations, patchInFlight: inFlight };
        replaceDraft(current);
        await persistenceQueueRef.current;
      }
      const inFlight = current.patchInFlight;
      if (!inFlight || (Object.keys(inFlight.changes).length === 0 && Object.keys(inFlight.observations).length === 0)) return true;
      setSaving(true);
      try {
        const result = await patchPartnerChecklist(token, inFlight.changes, inFlight.observations, inFlight.requestId, inFlight.expectedRevision);
        revisionRef.current = result.revision;
        const latestSession = sessionRef.current;
        if (latestSession) updateSession({
          ...latestSession,
          revision: result.revision,
          values: { ...latestSession.values, ...inFlight.changes },
          observations: { ...latestSession.observations, ...inFlight.observations },
        });
        const latestDraft = draftRef.current;
        if (latestDraft) replaceDraft({
          ...latestDraft,
          values: { ...latestDraft.values, ...inFlight.changes },
          observations: { ...latestDraft.observations, ...inFlight.observations },
          patchInFlight: null,
        });
        setOnlineState(true);
        setFailure(null);
        void attemptPendingDiagnostic();
      } catch (error) {
        setOnlineState(getOnlineState());
        if (isPartnerInactive(error)) {
          await deletePartnerDraft(key).catch(() => undefined);
          replaceDraft(null, false);
        } else if (isPartnerConflict(error)) {
          try {
            await requeueAfterConflict(key);
            setConflictNeedsReview(true);
          } catch (refreshError) {
            noteFailure(refreshError, 'save', inFlight.requestId, () => { void flushPendingChanges(); });
            return false;
          }
        } else if (isPartnerForbidden(error)) {
          try {
            await resolveAndMerge(key);
          } catch {
            // Mantém a alteração local até uma nova leitura confirmar as permissões.
          }
        }
        noteFailure(error, 'save', inFlight.requestId, () => {
          setConflictNeedsReview(false);
          void flushPendingChanges();
        });
        return false;
      } finally {
        if (mountedRef.current) setSaving(false);
      }
    }
  }), [attemptPendingDiagnostic, enqueue, noteFailure, replaceDraft, requeueAfterConflict, resolveAndMerge, setOnlineState, token, updateSession]);

  const updateFieldValue = useCallback((fieldId: string, value: string | null) => {
    if (closingSession.current || draftRef.current?.completionRequestId) return;
    const current = draftRef.current;
    if (!current || !editableIds.has(fieldId)) return;
    replaceDraft({
      ...current,
      values: { ...current.values, [fieldId]: value },
      pendingChanges: { ...current.pendingChanges, [fieldId]: value },
    });
    setRequiredWarning(false);
  }, [editableIds, replaceDraft]);

  const updateObservation = useCallback((fieldId: string, value: string | null) => {
    if (closingSession.current || draftRef.current?.completionRequestId) return;
    const current = draftRef.current;
    if (!current || !editableIds.has(fieldId)) return;
    replaceDraft({
      ...current,
      observations: { ...current.observations, [fieldId]: value },
      pendingObservations: { ...current.pendingObservations, [fieldId]: value },
    });
  }, [editableIds, replaceDraft]);

  const processPendingMedia = useCallback((fieldId: string): Promise<boolean> => enqueue(async () => {
    const key = draftKeyRef.current;
    let current = draftRef.current;
    const media = current?.pendingMedia[fieldId];
    if (!key || !media || media.needsRebase || !editableIds.has(fieldId)) return false;
    setMediaBusy((previous) => new Set(previous).add(fieldId));
    try {
      let uploadId = media.uploadId;
      let prepared: PartnerMediaPreparation | null = null;

      if (media.stage !== 'confirm') {
        prepared = await preparePartnerMedia(
          token,
          fieldId,
          media.mimeType,
          media.prepareRequestId,
          media.prepareExpectedRevision
        );
        uploadId = prepared.uploadId;
        revisionRef.current = prepared.revision;
        current = draftRef.current!;
        const nextMedia: PartnerPendingMedia = {
          ...media,
          uploadId,
          confirmExpectedRevision: prepared.revision,
          stage: 'upload',
        };
        replaceDraft({ ...current, pendingMedia: { ...current.pendingMedia, [fieldId]: nextMedia } });
        await persistenceQueueRef.current;
      }

      if (media.stage !== 'confirm') {
        if (!prepared) {
          prepared = await preparePartnerMedia(
            token,
            fieldId,
            media.mimeType,
            media.prepareRequestId,
            media.prepareExpectedRevision
          );
          uploadId = prepared.uploadId;
          revisionRef.current = prepared.revision;
        }
        try {
          await uploadPartnerMedia(prepared, media.blob, media.mimeType);
        } catch (uploadError) {
          if (!isPartnerUploadAlreadyExists(uploadError)) throw uploadError;
        }
        current = draftRef.current!;
        const stagedMedia = current.pendingMedia[fieldId];
        if (!stagedMedia) return false;
        replaceDraft({
          ...current,
          pendingMedia: {
            ...current.pendingMedia,
            [fieldId]: { ...stagedMedia, uploadId, confirmExpectedRevision: prepared.revision, stage: 'confirm' },
          },
        });
        await persistenceQueueRef.current;
      }

      const latestMedia = draftRef.current?.pendingMedia[fieldId];
      if (!latestMedia?.uploadId) throw new Error('O envio não gerou uma referência válida.');
      const confirmed = await confirmPartnerMedia(
        token,
        latestMedia.uploadId,
        latestMedia.confirmRequestId,
        latestMedia.confirmExpectedRevision ?? latestMedia.prepareExpectedRevision
      );
      revisionRef.current = confirmed.revision;
      const fresh = await resolvePartnerChecklist(token);
      const afterResolve = draftRef.current ?? makeEmptyPartnerDraft(key, fresh.share_link.expires_at);
      const pendingMedia = { ...afterResolve.pendingMedia };
      delete pendingMedia[fieldId];
      // Só remover a pendência permite à reconciliação adotar a mídia confirmada.
      replaceDraft(reconcileDraftWithState({ ...afterResolve, pendingMedia }, fresh, key));
      updateSession(fresh);
      setOnlineState(true);
      setFailure(null);
      if (fresh.fields.some(field => field.id === fieldId && isSignatureField(field.tipo))) {
        showToast('Assinatura salva e enviada.', { type: 'success', duration: 1800 });
      }
      void attemptPendingDiagnostic();
      return true;
    } catch (error) {
      const currentMedia = draftRef.current?.pendingMedia[fieldId];
      const requestId = currentMedia?.stage === 'confirm'
        ? currentMedia.confirmRequestId
        : currentMedia?.prepareRequestId ?? uuidv4();
      setOnlineState(classifyPartnerDiagnosticCode(error) !== 'network_error');
      if (isPartnerInactive(error) && key) {
        await deletePartnerDraft(key).catch(() => undefined);
        replaceDraft(null, false);
      } else if (isPartnerConflict(error)) {
        try {
          const fresh = await resolvePartnerChecklist(token);
          const currentDraft = draftRef.current ?? makeEmptyPartnerDraft(key, fresh.share_link.expires_at);
          const allowed = editableFieldIds(usableFields(fresh.fields));
          if (allowed.has(fieldId)) {
            const nextMedia = currentDraft.pendingMedia[fieldId];
            const reconciled = reconcileDraftWithState(currentDraft, fresh, key);
            if (nextMedia) {
              replaceDraft({
                ...reconciled,
                pendingMedia: {
                  ...reconciled.pendingMedia,
                  [fieldId]: { ...nextMedia, stage: 'confirm', needsRebase: true },
                },
              });
            }
          }
          updateSession(fresh);
          setOnlineState(true);
        } catch {
          // A confirmação continua pendente e permanece guardada para nova tentativa manual.
        }
      } else if (isPartnerForbidden(error)) {
        try {
          await resolveAndMerge(key);
        } catch {
          // Mantém o rascunho até o servidor confirmar novamente as permissões.
        }
      }
      noteFailure(error, currentMedia?.stage === 'prepare' ? 'upload_prepare' : currentMedia?.stage === 'confirm' ? 'upload_confirm' : 'upload_transfer', requestId, () => {
        retryMediaRef.current(fieldId);
      });
      return false;
    } finally {
      setMediaBusy((previous) => {
        const next = new Set(previous);
        next.delete(fieldId);
        return next;
      });
    }
  }), [attemptPendingDiagnostic, editableIds, enqueue, noteFailure, replaceDraft, resolveAndMerge, setOnlineState, token, updateSession]);

  const requestMediaRetry = useCallback((fieldId: string) => {
    const pending = draftRef.current?.pendingMedia[fieldId];
    if (!pending) return;
    if (pending.needsRebase) {
      setMediaRebaseFieldId(fieldId);
      return;
    }
    void processPendingMedia(fieldId);
  }, [processPendingMedia]);

  retryMediaRef.current = requestMediaRetry;

  const confirmMediaRebase = useCallback(() => {
    const fieldId = mediaRebaseFieldId;
    const current = draftRef.current;
    const pending = fieldId ? current?.pendingMedia[fieldId] : null;
    if (!fieldId || !current || !pending || !editableIds.has(fieldId)) return;
    const hasPreparedObject = Boolean(pending.uploadId);
    const rebased: PartnerPendingMedia = {
      ...pending,
      needsRebase: false,
      prepareExpectedRevision: hasPreparedObject ? pending.prepareExpectedRevision : revisionRef.current,
      confirmExpectedRevision: hasPreparedObject ? revisionRef.current : null,
      stage: hasPreparedObject ? 'confirm' : 'prepare',
      confirmRequestId: uuidv4(),
      ...(hasPreparedObject ? {} : { prepareRequestId: uuidv4() }),
    };
    replaceDraft({ ...current, pendingMedia: { ...current.pendingMedia, [fieldId]: rebased } });
    setMediaRebaseFieldId(null);
    void processPendingMedia(fieldId);
  }, [editableIds, mediaRebaseFieldId, processPendingMedia, replaceDraft]);

  const stageMedia = useCallback(async (fieldId: string, blob: Blob, mimeType: string) => {
    if (closingSession.current || draftRef.current?.completionRequestId) return;
    if (!editableIds.has(fieldId)) return;
    const started = sessionRef.current;
    const startedKey = draftKeyRef.current;
    const startedToken = activeToken.current;
    const field = started?.fields.find(item => item.id === fieldId);
    if (!started || !startedKey || !field || field.permission !== 'edit' || started.share_link.status !== 'active') return;
    const sameScope = () => {
      const latest = sessionRef.current;
      if (!latest) return false;
      return mountedRef.current && !draftRef.current?.completionRequestId && activeToken.current === startedToken && draftKeyRef.current === startedKey
        && latest.checklist.id === started.checklist.id && latest.share_link.id === started.share_link.id
        && latest.share_link.status === 'active' && latest.fields.some(item => item.id === fieldId && item.permission === 'edit');
    };
    const normalizePhoto = Platform.OS === 'web' && isPhotoField(field.tipo);
    if (preparingPhotos.current.has(fieldId)) return;
    let ready = false;
    if (normalizePhoto) {
      preparingPhotos.current.add(fieldId); setPreparingPhotoCount(preparingPhotos.current.size);
    }
    try {
      const preparedBlob = normalizePhoto ? await prepareChecklistPhotoBlobWeb(blob) : blob;
      const preparedMime = normalizePhoto ? 'image/jpeg' : mimeType;
      if (!sameScope()) return;
      if (!/^image\/(jpeg|png|webp)$/i.test(preparedMime)) throw new Error('Esse formato de imagem não é aceito.');
      if (preparedBlob.size < 1 || preparedBlob.size > MAX_PARTNER_MEDIA_BYTES) throw new Error('A imagem excede o limite permitido. Escolha uma imagem menor.');
      // Após a preparação, reler o draft para não apagar edições feitas em outros campos.
      const current = draftRef.current;
      if (!current) return;
      const media: PartnerPendingMedia = {
        blob: preparedBlob, mimeType: preparedMime, prepareRequestId: uuidv4(), confirmRequestId: uuidv4(),
        prepareExpectedRevision: revisionRef.current, confirmExpectedRevision: null, stage: 'prepare',
      };
      replaceDraft({ ...current, pendingMedia: { ...current.pendingMedia, [fieldId]: media } });
      setFailure(null); ready = true;
    } catch (error) {
      if (sameScope()) setFailure({ message: error instanceof Error ? error.message : 'Não foi possível preparar a foto. Tente novamente.',
        protocol: null, retry: () => { void stageMedia(fieldId, blob, mimeType); } });
    } finally {
      if (normalizePhoto) {
        preparingPhotos.current.delete(fieldId);
        if (mountedRef.current) setPreparingPhotoCount(preparingPhotos.current.size);
      }
    }
    if (!ready) return;
    void processPendingMedia(fieldId);
  }, [editableIds, processPendingMedia, replaceDraft]);

  const removeMedia = useCallback((fieldId: string) => {
    updateFieldValue(fieldId, null);
  }, [updateFieldValue]);

  const flushAllPending = useCallback(async (): Promise<boolean> => {
    const changesSaved = await flushPendingChanges();
    if (!changesSaved) return false;
    const pendingIds = Object.keys(draftRef.current?.pendingMedia ?? {});
    for (const fieldId of pendingIds) {
      const mediaSaved = await processPendingMedia(fieldId);
      if (!mediaSaved) return false;
    }
    return !hasPartnerDraftWork(draftRef.current);
  }, [flushPendingChanges, processPendingMedia]);

  const finishPartnerSession = useCallback(async (action: PartnerSessionAction) => {
    if (preparingPhotos.current.size > 0 || closingSession.current) return;
    closingSession.current = true; setClosing(true);
    try {
      setFailure(null);
      setConflictNeedsReview(false);
      let currentSession = sessionRef.current;
      if (!currentSession || currentSession.share_link.status !== 'active' ||
          (action === 'complete' && !currentSession.allow_complete)) return;
      const key = draftKeyRef.current;
      if (!key) return;

      if (terminalNeedsRefreshRef.current) {
        try {
          await resolveAndMerge(key);
          terminalNeedsRefreshRef.current = false;
        } catch (error) {
          noteFailure(error, 'resolve', uuidv4(), () => retryTerminalRef.current(action));
          return;
        }
        currentSession = sessionRef.current;
        if (!currentSession || currentSession.share_link.status !== 'active' ||
            (action === 'complete' && !currentSession.allow_complete)) return;
      }

      if (action === 'complete') {
        const requiredMissing = usableFields(currentSession.fields).some((field) => {
          if (!field.obrigatorio || !editableIds.has(field.id)) return false;
          const value = valueFromDraftOrServer(draftRef.current, currentSession!, field.id);
          const hasPendingMedia = Boolean(draftRef.current?.pendingMedia[field.id]);
          return !hasPendingMedia && (value === null || value.trim() === '');
        });
        if (requiredMissing) {
          setRequiredWarning(true);
          setFailure({ message: 'Preencha os campos obrigatórios visíveis antes de concluir.', protocol: null,
            retry: () => retryTerminalRef.current(action) });
          return;
        }
      }

      if (!await flushAllPending()) return;
      currentSession = sessionRef.current;
      if (!currentSession || currentSession.share_link.status !== 'active') return;
      let currentDraft = draftRef.current ?? makeEmptyPartnerDraft(key, currentSession.share_link.expires_at);
      const pendingMode = currentDraft.completionMode ?? (currentDraft.completionRequestId ? 'submit' : null);
      if (currentDraft.completionRequestId && pendingMode !== action) {
        setFailure({
          message: 'Há um envio anterior sem confirmação. Confirme esse pedido antes de iniciar outra ação.',
          protocol: currentDraft.completionRequestId,
          retry: () => retryTerminalRef.current(pendingMode ?? 'submit'),
        });
        return;
      }
      if (!currentDraft.completionRequestId) {
        currentDraft = {
          ...currentDraft,
          completionRequestId: uuidv4(),
          completionExpectedRevision: revisionRef.current,
          completionMode: action,
        };
        replaceDraft(currentDraft);
        await persistenceQueueRef.current;
      }
      const requestId = currentDraft.completionRequestId!;
      const expectedRevision = currentDraft.completionExpectedRevision ?? revisionRef.current;
      const operation = currentDraft.completionMode ?? pendingMode ?? action;
      setCompleting(true);
      try {
        const result = operation === 'submit'
          ? await submitPartnerChecklist(token, requestId, expectedRevision)
          : await completePartnerChecklist(token, requestId, expectedRevision);
        if (result.session_status !== 'completed' ||
            (result.checklist_status !== 'in_progress' && result.checklist_status !== 'done')) {
          throw new Error('Resposta de encerramento inválida.');
        }
        revisionRef.current = result.revision;
        const terminalSession: PartnerChecklistState = {
          ...currentSession,
          revision: result.revision,
          share_link: { ...currentSession.share_link, status: result.session_status },
          checklist: { ...currentSession.checklist, status: result.checklist_status },
        };
        updateSession(terminalSession);
        const next = reconcileDraftWithState(draftRef.current ?? currentDraft, terminalSession, key);
        replaceDraft({
          ...next,
          completionRequestId: null,
          completionExpectedRevision: null,
          completionMode: null,
          terminalChecklistStatus: result.checklist_status,
          terminalRevision: result.revision,
        });
        terminalNeedsRefreshRef.current = false;
        setFailure(null);
        setOnlineState(true);
        setSuccessAction(result.checklist_status === 'done' ? 'complete'
          : currentSession.fields.some(field => field.permission === 'edit') ? 'submit' : 'consult');
        void attemptPendingDiagnostic();
      } catch (error) {
        setOnlineState(classifyPartnerDiagnosticCode(error) !== 'network_error');
        if (isPartnerConflict(error) || isPartnerRequiredPending(error)) {
          const latest = draftRef.current ?? currentDraft;
          replaceDraft({ ...latest, completionRequestId: null, completionExpectedRevision: null, completionMode: null });
          if (isPartnerConflict(error)) {
            try {
              await resolveAndMerge(key);
              terminalNeedsRefreshRef.current = false;
            } catch {
              terminalNeedsRefreshRef.current = true;
            }
          }
        }
        noteFailure(error, 'complete', requestId, () => retryTerminalRef.current(action));
        if (isPartnerInactive(error)) {
          await deletePartnerDraft(key).catch(() => undefined);
          replaceDraft(null, false);
        }
      } finally {
        setCompleting(false);
      }
    } finally {
      closingSession.current = false;
      if (mountedRef.current) setClosing(false);
    }
  }, [attemptPendingDiagnostic, classifyPartnerDiagnosticCode, completePartnerChecklist, editableIds, flushAllPending, noteFailure, replaceDraft, resolveAndMerge, setOnlineState, submitPartnerChecklist, token, updateSession]);

  retryTerminalRef.current = (action) => { void finishPartnerSession(action); };

  const handleSubmit = useCallback(() => finishPartnerSession('submit'), [finishPartnerSession]);
  const handleComplete = useCallback(() => finishPartnerSession('complete'), [finishPartnerSession]);

  const onSignatureSaved = useCallback(async (dataUrl: string) => {
    const fieldId = signatureField?.id;
    setSignatureField(null);
    if (!fieldId) return;
    try {
      const blob = await dataUrlToPartnerBlob(dataUrl);
      void stageMedia(fieldId, blob, blob.type || 'image/png');
    } catch {
      setFailure({
        message: 'Não foi possível preparar a assinatura. Tente assinar novamente.',
        protocol: null,
        retry: () => setSignatureField(signatureField),
      });
    }
  }, [signatureField, stageMedia]);

  const saveMark = useCallback(async () => {
    const fieldId = markField?.id;
    if (!fieldId || !markPadRef.current) return;
    try {
      const captured = await markPadRef.current.capture();
      const blob = await dataUrlToPartnerBlob(captured);
      setMarkField(null);
      setMarkPoints([]);
      void stageMedia(fieldId, blob, blob.type || 'image/png');
    } catch {
      setFailure({ message: 'Não foi possível preparar a marcação. Tente novamente.', protocol: null, retry: () => retryMarkRef.current() });
    }
  }, [markField, stageMedia]);

  retryMarkRef.current = () => { void saveMark(); };

  const renderReadonlyMedia = (field: PartnerChecklistField, value: string | null) => (
    <View style={styles.readonlyMedia}>
      {value ? <Image source={{ uri: value }} style={styles.previewImage} resizeMode="contain" /> : (
        <Text style={styles.readonlyValue}>Nenhum arquivo enviado</Text>
      )}
    </View>
  );

  const renderField = (field: PartnerChecklistField) => {
    const currentSession = session;
    if (!currentSession) return null;
    const value = valueFromDraftOrServer(draft, currentSession, field.id);
    const canEdit = field.permission === 'edit' && field.editable !== false && editableIds.has(field.id) && !closing && !draft?.completionRequestId && currentSession.share_link.status === 'active';
    const pendingMedia = draft?.pendingMedia[field.id];
    const mediaIsBusy = mediaBusy.has(field.id) || preparingPhotos.current.has(field.id);
    const label = `${field.nome}${field.obrigatorio ? ' *' : ''}`;
    const observation = !currentSession.receipt && draft && Object.prototype.hasOwnProperty.call(draft.observations, field.id)
      ? draft.observations[field.id]
      : serverObservationFor(currentSession, field.id);
    const commonInput = {
      value: value ?? '',
      onChangeText: (next: string) => updateFieldValue(field.id, next),
      editable: canEdit,
      placeholder: field.nome,
      placeholderTextColor: theme.colors.text.tertiary,
      style: [styles.input, !canEdit && styles.inputDisabled],
    };
    const renderObservation = () => {
      if (!canEdit && !observation) return null;
      const expanded = expandedObservationField === field.id;
      return <View style={styles.observationBlock}>
        {observation && !expanded && <Text style={styles.observationValue}>Observação: {observation}</Text>}
        {canEdit && (expanded ? <TextInput
          value={observation ?? ''}
          onChangeText={(text) => updateObservation(field.id, text.trim() ? text : null)}
          multiline
          numberOfLines={3}
          maxLength={10000}
          textAlignVertical="top"
          placeholder="Adicionar observação"
          placeholderTextColor={theme.colors.text.tertiary}
          style={[styles.input, styles.observationInput]}
          accessibilityLabel={`Observação: ${field.nome}`}
        /> : <Pressable onPress={() => setExpandedObservationField(field.id)}>
          <Text style={styles.observationAction}>{observation ? 'Editar observação' : 'Adicionar observação'}</Text>
        </Pressable>)}
      </View>;
    };

    if (isPhotoField(field.tipo)) {
      return <View key={field.id} style={styles.fieldBlock}>
        <Text style={styles.fieldLabel}>{label}</Text>
        {canEdit ? <PartnerPhotoField
          key={`${token}:${session.share_link.id}:${field.id}`}
          value={value}
          editable
          busy={mediaIsBusy || Boolean(pendingMedia)}
          onSelect={(blob, mimeType) => { void stageMedia(field.id, blob, mimeType); }}
          onRemove={() => removeMedia(field.id)}
        /> : renderReadonlyMedia(field, value)}
        {pendingMedia && <View style={styles.pendingMediaRow}>
          <Text style={styles.pendingText}>Envio pendente</Text>
          <Pressable disabled={mediaIsBusy} onPress={() => requestMediaRetry(field.id)}>
            <Text style={styles.retryText}>{mediaIsBusy ? 'Enviando…' : pendingMedia.needsRebase ? 'Revisar e tentar novamente' : 'Tentar novamente'}</Text>
          </Pressable>
        </View>}
        {renderObservation()}
      </View>;
    }

    if (isSignatureField(field.tipo)) {
      return <View key={field.id} style={styles.fieldBlock}>
        <Text style={styles.fieldLabel}>{label}</Text>
        {value ? <Image source={{ uri: value }} style={styles.signaturePreview} resizeMode="contain" /> : (
          <Text style={styles.readonlyValue}>Nenhuma assinatura enviada</Text>
        )}
        {canEdit && <Pressable disabled={mediaIsBusy} style={styles.outlineButton} onPress={() => setSignatureField(field)}>
          <Text style={styles.outlineButtonText}>{value ? 'Refazer assinatura' : 'Assinar'}</Text>
        </Pressable>}
        {pendingMedia && <View style={styles.pendingMediaRow}>
          <Text style={styles.pendingText}>Envio pendente</Text>
          <Pressable disabled={mediaIsBusy} onPress={() => requestMediaRetry(field.id)}>
            <Text style={styles.retryText}>{mediaIsBusy ? 'Enviando…' : pendingMedia.needsRebase ? 'Revisar e tentar novamente' : 'Tentar novamente'}</Text>
          </Pressable>
        </View>}
        {renderObservation()}
      </View>;
    }

    if (isMarkField(field.tipo)) {
      const background = field.background_url ?? null;
      return <View key={field.id} style={styles.fieldBlock}>
        <Text style={styles.fieldLabel}>{label}</Text>
        {value ? <Image source={{ uri: value }} style={styles.previewImage} resizeMode="contain" /> : (
          <Text style={styles.readonlyValue}>Nenhuma marcação enviada</Text>
        )}
        {canEdit && (background ? <Pressable disabled={mediaIsBusy} style={styles.outlineButton} onPress={() => { setMarkPoints([]); setMarkField(field); }}>
          <Text style={styles.outlineButtonText}>{value ? 'Refazer marcação' : 'Marcar imagem'}</Text>
        </Pressable> : <Text style={styles.helperText}>Imagem de referência indisponível.</Text>)}
        {pendingMedia && <View style={styles.pendingMediaRow}>
          <Text style={styles.pendingText}>Envio pendente</Text>
          <Pressable disabled={mediaIsBusy} onPress={() => requestMediaRetry(field.id)}>
            <Text style={styles.retryText}>{mediaIsBusy ? 'Enviando…' : pendingMedia.needsRebase ? 'Revisar e tentar novamente' : 'Tentar novamente'}</Text>
          </Pressable>
        </View>}
        {renderObservation()}
      </View>;
    }

    if (isDropdownField(field.tipo)) {
      const options = currentSession.dropdown_options?.[field.id] ?? currentSession.dropdown_options?.[field.tipo] ?? [];
      return <View key={field.id} style={styles.fieldBlock}>
        <Text style={styles.fieldLabel}>{label}</Text>
        {options.length ? <View style={styles.chips}>
          {options.map((option) => <Pressable
            key={`${field.id}-${option}`}
            accessibilityRole="button"
            disabled={!canEdit}
            onPress={() => updateFieldValue(field.id, option)}
            style={[styles.chip, value === option && styles.chipSelected, !canEdit && styles.chipDisabled]}
          ><Text style={[styles.chipText, value === option && styles.chipTextSelected]}>{option}</Text></Pressable>)}
        </View> : <TextInput {...commonInput} />}
        {renderObservation()}
      </View>;
    }

    if (isDateField(field.tipo)) {
      return canEdit ? <View key={field.id} style={styles.fieldBlock}>
        <ChecklistDateTimeField field={field} value={value ?? undefined} onChange={(next) => updateFieldValue(field.id, next)} />
        {renderObservation()}
      </View> : <View key={field.id} style={styles.fieldBlock}>
        <Text style={styles.fieldLabel}>{label}</Text>
        <Text style={styles.readonlyValue}>{value || '—'}</Text>
        {renderObservation()}
      </View>;
    }

    return <View key={field.id} style={styles.fieldBlock}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextInput
        {...commonInput}
        multiline={isMessageField(field.tipo)}
        numberOfLines={isMessageField(field.tipo) ? 4 : 1}
        textAlignVertical={isMessageField(field.tipo) ? 'top' : 'center'}
        keyboardType={isNumberField(field.tipo) || isCepField(field.tipo) ? 'numeric' : 'default'}
        maxLength={isCepField(field.tipo) ? 9 : undefined}
        accessibilityLabel={field.nome}
      />
      {renderObservation()}
    </View>;
  };

  const statusLabel = session?.share_link.status === 'active' ? 'Acesso ativo'
    : session?.share_link.status === 'completed'
      ? session.checklist.status === 'done' ? 'Checklist concluído' : 'Preenchimento encerrado'
    : session?.share_link.status === 'expired' ? 'Acesso expirado'
    : session?.share_link.status === 'revoked' ? 'Acesso encerrado'
    : 'Acesso de parceiro';
  const canEditAny = session?.share_link.status === 'active' && visibleFields.some((field) => editableIds.has(field.id));
  const completed = session?.share_link.status === 'completed';
  const terminalRequestPending = Boolean(draft?.completionRequestId);
  const checklistCode = session?.checklist.codigo || session?.checklist.sequencial_codigo || session?.checklist.sequencial_numero;

  return (
    <SafeAreaView style={styles.screen}>
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <View style={styles.header} testID="partner-fixed-header">
            <Text style={styles.eyebrow}>APP GUINCHO · PARCEIRO</Text>
            <View style={styles.headerRow}>
            <Text accessibilityRole="header" numberOfLines={2} style={styles.title}>{session?.empresa?.nome || 'Checklist compartilhado'}</Text>
            <View style={[styles.statusPill, completed && styles.statusPillComplete]}>
              <View style={[styles.statusDot, completed && styles.statusDotComplete]} />
              <Text style={styles.statusText}>{statusLabel}</Text>
            </View>
            </View>
            <Text style={styles.subtitle}>Campos compartilhados{checklistCode ? ` · Checklist ${checklistCode}` : ''}</Text>
          </View>

        <ScrollView contentContainerStyle={[styles.content, { paddingBottom: Math.max(insets.bottom, theme.spacing.lg) + 28 }]} keyboardShouldPersistTaps="handled">

          {loading ? <View style={styles.loadingCard}><ActivityIndicator color={theme.colors.primary.default} /><Text style={styles.helperText}>Carregando checklist…</Text></View> : null}

          {persistenceFailed && <View style={styles.localWarning}>
            <Text style={styles.localWarningText}>Não foi possível salvar o rascunho neste dispositivo.</Text>
          </View>}

          {requiredWarning && <View style={styles.localWarning}>
            <Text style={styles.localWarningText}>Preencha os campos obrigatórios visíveis antes de concluir.</Text>
          </View>}

          {session && groups.map((group) => <View key={group.title} style={styles.groupCard}>
            <Text style={styles.groupTitle}>{group.title}</Text>
            {groupChecklistRows(group.fields, columns).map(row => <View key={row.id} style={styles.fieldRow} testID={row.fullWidth ? 'partner-row-full' : 'partner-row-pair'}>
              {(row.cells ?? [row]).map(field => <View key={field.id} style={styles.fieldCell}>{renderField(field)}</View>)}
            </View>)}
          </View>)}

          {session && !canEditAny && !completed && <View style={styles.readonlyNotice}>
            <Text style={styles.readonlyNoticeText}>Este acesso permite consultar os campos compartilhados. Ao terminar, encerre a consulta para liberar o checklist e baixar o comprovante.</Text>
          </View>}

          <Text style={styles.footerNote}>{PARTNER_FOOTER}</Text>
        </ScrollView>

        {session?.share_link.status === 'active' && <View style={[styles.actionBar, { paddingBottom: Math.max(insets.bottom, theme.spacing.sm) }]}>
          <Pressable disabled={saving || closing || terminalRequestPending || preparingPhotoCount > 0} onPress={() => { void handleSubmit(); }} style={[styles.secondaryButton, (saving || closing || terminalRequestPending || preparingPhotoCount > 0) && styles.buttonDisabled]}>
            {saving ? <ActivityIndicator color={theme.colors.primary.default} /> : <Text style={styles.secondaryButtonText}>{canEditAny ? 'Enviar preenchimento' : 'Encerrar consulta'}</Text>}
          </Pressable>
          {session.allow_complete && <Pressable disabled={saving || closing || terminalRequestPending || preparingPhotoCount > 0} onPress={() => { void handleComplete(); }} style={[styles.primaryButton, (saving || closing || terminalRequestPending || preparingPhotoCount > 0) && styles.buttonDisabled]}>
            {completing ? <ActivityIndicator color={theme.colors.white} /> : <Text style={styles.primaryButtonText}>Concluir checklist</Text>}
          </Pressable>}
          {hasChecklistPending && <Text style={styles.pendingStatus}>Alterações aguardando envio</Text>}
          {preparingPhotoCount > 0 && <Text style={styles.pendingStatus}>Preparando foto para envio…</Text>}
          {closing && <Text style={styles.pendingStatus}>Confirmando envio…</Text>}
          {terminalRequestPending && !saving && !closing && <Pressable style={styles.outlineButton}
            disabled={preparingPhotoCount > 0} onPress={() => retryTerminalRef.current(draft?.completionMode ?? 'submit')}>
            <Text style={styles.outlineButtonText}>Confirmar envio pendente</Text>
          </Pressable>}
        </View>}
        {session && completed && <View style={[styles.actionBar, { paddingBottom: Math.max(insets.bottom, theme.spacing.sm) }]}>
          <Text style={styles.helperText}>Comprovante apenas dos campos compartilhados, disponível até {new Date(session.share_link.expires_at).toLocaleString('pt-BR')}.</Text>
          <Pressable disabled={pdfBusy} onPress={() => { void downloadReceipt(); }} style={[styles.primaryButton, pdfBusy && styles.buttonDisabled]}>
            <Text style={styles.primaryButtonText}>{pdfBusy ? 'Gerando PDF…' : 'Baixar PDF dos campos compartilhados'}</Text>
          </Pressable>
        </View>}
      </KeyboardAvoidingView>

      {failure && <Modal visible={!signatureField && !markField && !mediaRebaseFieldId && !successAction} transparent animationType="fade" onRequestClose={() => setFailure(null)}>
        <View style={styles.confirmOverlay}><View style={styles.confirmCard} accessibilityViewIsModal testID="partner-operation-error">
          <Text accessibilityRole="header" style={styles.modalTitle}>Não foi possível continuar</Text>
          <Text accessibilityRole="alert" style={styles.failureText}>{failure.message}</Text>
          {failure.protocol && <Text style={styles.protocolText}>Protocolo: {failure.protocol}</Text>}
          <Pressable style={styles.primaryButton} onPress={() => { const retry = failure.retry; setFailure(null); retry(); }}><Text style={styles.primaryButtonText}>Tentar novamente</Text></Pressable>
          <Pressable style={styles.secondaryButton} onPress={() => setFailure(null)}><Text style={styles.secondaryButtonText}>Agora não</Text></Pressable>
        </View></View>
      </Modal>}

      {successAction && <Modal visible transparent animationType="fade" onRequestClose={() => setSuccessAction(null)}>
        <View style={styles.confirmOverlay}><View style={styles.confirmCard} accessibilityViewIsModal>
          <Text accessibilityRole="header" style={styles.modalTitle}>{successAction === 'complete' ? 'Checklist concluído' : successAction === 'consult' ? 'Consulta encerrada' : 'Preenchimento enviado'}</Text>
          <Text style={styles.helperText}>{successAction === 'complete'
            ? 'A conclusão foi confirmada. Este acesso de edição foi encerrado.'
            : successAction === 'consult' ? 'A consulta foi encerrada e o checklist foi liberado para a empresa.'
            : 'O envio foi confirmado. O checklist continua em andamento para a empresa conferir e concluir.'}</Text>
          <Text style={styles.helperText}>Você pode baixar o comprovante dos campos compartilhados até a validade original do link.</Text>
          <Pressable style={styles.primaryButton} onPress={() => setSuccessAction(null)}><Text style={styles.primaryButtonText}>Entendi</Text></Pressable>
        </View></View>
      </Modal>}

      <Modal visible={Boolean(signatureField)} animationType="slide" onRequestClose={() => setSignatureField(null)}>
        <SafeAreaView style={styles.modalScreen}>
          <SignaturePad onOK={(dataUrl) => { void onSignatureSaved(dataUrl); }} onClose={() => setSignatureField(null)} />
        </SafeAreaView>
      </Modal>

      <Modal visible={Boolean(markField)} animationType="slide" onRequestClose={() => setMarkField(null)}>
        <SafeAreaView style={styles.modalScreen}>
          <View style={styles.markHeader}>
            <Text style={styles.modalTitle}>Marcar imagem</Text>
            <Pressable onPress={() => setMarkField(null)}><Text style={styles.cancelText}>Cancelar</Text></Pressable>
          </View>
          {markField?.background_url ? <InlineMarkPad
            ref={markPadRef}
            fieldId={markField.id}
            sourceUri={markField.background_url}
            previewScopeKey={`partner-${markField.id}`}
            points={markPoints}
            onPointsChange={setMarkPoints}
            onInteraction={() => true}
          /> : <Text style={styles.helperText}>Imagem de referência indisponível.</Text>}
          <Pressable style={styles.primaryButton} onPress={() => { void saveMark(); }}>
            <Text style={styles.primaryButtonText}>Salvar marcação</Text>
          </Pressable>
        </SafeAreaView>
      </Modal>

      <Modal visible={Boolean(mediaRebaseFieldId)} transparent animationType="fade" onRequestClose={() => setMediaRebaseFieldId(null)}>
        <View style={styles.confirmOverlay}>
          <View style={styles.confirmCard}>
            <Text style={styles.modalTitle}>Dados atualizados</Text>
            <Text style={styles.helperText}>O checklist mudou durante o envio. Revise e autorize a aplicação desta mídia sobre a versão atual.</Text>
            <View style={styles.confirmActions}>
              <Pressable onPress={() => setMediaRebaseFieldId(null)} style={styles.secondaryButton}>
                <Text style={styles.secondaryButtonText}>Agora não</Text>
              </Pressable>
              <Pressable onPress={confirmMediaRebase} style={styles.primaryButton}>
                <Text style={styles.primaryButtonText}>Reenviar mídia</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

export default PartnerChecklistPage;

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: theme.colors.background.secondary },
  flex: { flex: 1 },
  content: { width: '100%', maxWidth: 820, alignSelf: 'center', padding: theme.spacing.md, gap: theme.spacing.md },
  header: { backgroundColor: theme.colors.background.primary, width: '100%', maxWidth: 820, alignSelf: 'center', paddingHorizontal: theme.spacing.md, paddingVertical: theme.spacing.sm, gap: 4, borderBottomWidth: 1, borderColor: theme.colors.border.light },
  headerRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: theme.spacing.sm },
  eyebrow: { color: theme.colors.primary.default, fontSize: theme.fontSizes.xs, fontWeight: '700', letterSpacing: 0.8 },
  title: { flexGrow: 1, flexShrink: 1, minWidth: 0, color: theme.colors.text.primary, fontSize: theme.fontSizes.lg, fontWeight: '700' },
  subtitle: { color: theme.colors.text.secondary, fontSize: theme.fontSizes.sm, lineHeight: 21 },
  statusPill: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', gap: theme.spacing.xs, paddingHorizontal: theme.spacing.sm, paddingVertical: 6, borderRadius: theme.borderRadius.pill, backgroundColor: '#EFF6FF' },
  statusPillComplete: { backgroundColor: '#ECFDF5' },
  statusDot: { width: 7, height: 7, borderRadius: 4, backgroundColor: theme.colors.primary.default },
  statusDotComplete: { backgroundColor: '#15803D' },
  statusText: { color: theme.colors.text.secondary, fontSize: theme.fontSizes.xs, fontWeight: '600' },
  loadingCard: { backgroundColor: theme.colors.background.primary, borderRadius: theme.borderRadius.md, padding: theme.spacing.lg, alignItems: 'center', gap: theme.spacing.sm },
  helperText: { color: theme.colors.text.secondary, fontSize: theme.fontSizes.sm, lineHeight: 20 },
  failureCard: { padding: theme.spacing.md, backgroundColor: '#FFF7F7', borderColor: '#FECACA', borderWidth: 1, borderRadius: theme.borderRadius.md, gap: theme.spacing.xs },
  failureText: { color: '#991B1B', fontSize: theme.fontSizes.sm, fontWeight: '600' },
  protocolText: { color: theme.colors.text.secondary, fontSize: theme.fontSizes.xs },
  retryButton: { alignSelf: 'flex-start', paddingVertical: 7 },
  retryButtonText: { color: theme.colors.primary.dark, fontSize: theme.fontSizes.sm, fontWeight: '700' },
  localWarning: { padding: theme.spacing.md, borderRadius: theme.borderRadius.md, backgroundColor: '#FFFBEB', borderWidth: 1, borderColor: '#FDE68A' },
  localWarningText: { color: '#92400E', fontSize: theme.fontSizes.sm },
  groupCard: { backgroundColor: theme.colors.background.primary, borderRadius: theme.borderRadius.lg, padding: theme.spacing.md, gap: theme.spacing.md, borderWidth: 1, borderColor: theme.colors.border.light },
  groupTitle: { color: theme.colors.text.primary, fontSize: theme.fontSizes.lg, fontWeight: '700', paddingBottom: theme.spacing.xs, borderBottomWidth: 1, borderBottomColor: theme.colors.border.light },
  fieldBlock: { gap: theme.spacing.sm },
  fieldRow: { flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing.sm },
  fieldCell: { flex: 1, minWidth: 0 },
  fieldLabel: { color: theme.colors.text.primary, fontSize: theme.fontSizes.sm, fontWeight: '600' },
  input: { minHeight: 44, minWidth: 0, borderWidth: 1, borderColor: theme.colors.border.default, backgroundColor: theme.colors.background.primary, color: theme.colors.text.primary, borderRadius: theme.borderRadius.sm, paddingHorizontal: theme.spacing.md, paddingVertical: theme.spacing.sm, fontSize: theme.fontSizes.md },
  inputDisabled: { backgroundColor: theme.colors.background.secondary, color: theme.colors.text.secondary },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.sm },
  chip: { borderRadius: theme.borderRadius.pill, borderWidth: 1, borderColor: theme.colors.border.default, paddingHorizontal: theme.spacing.md, paddingVertical: theme.spacing.sm },
  chipSelected: { backgroundColor: theme.colors.primary.default, borderColor: theme.colors.primary.default },
  chipDisabled: { opacity: 0.7 },
  chipText: { color: theme.colors.text.secondary, fontSize: theme.fontSizes.sm },
  chipTextSelected: { color: theme.colors.white, fontWeight: '600' },
  readonlyValue: { color: theme.colors.text.secondary, fontSize: theme.fontSizes.sm, lineHeight: 20 },
  readonlyMedia: { gap: theme.spacing.sm },
  previewImage: { width: '100%', height: 220, borderRadius: theme.borderRadius.md, backgroundColor: theme.colors.background.tertiary },
  signaturePreview: { width: '100%', height: 130, borderRadius: theme.borderRadius.md, backgroundColor: theme.colors.background.secondary },
  outlineButton: { alignSelf: 'flex-start', borderColor: theme.colors.primary.default, borderWidth: 1, borderRadius: theme.borderRadius.sm, paddingHorizontal: theme.spacing.md, paddingVertical: theme.spacing.sm },
  outlineButtonText: { color: theme.colors.primary.dark, fontWeight: '600', fontSize: theme.fontSizes.sm },
  pendingMediaRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing.sm },
  pendingText: { color: '#92400E', fontSize: theme.fontSizes.xs },
  retryText: { color: theme.colors.primary.dark, fontWeight: '700', fontSize: theme.fontSizes.xs },
  observationBlock: { gap: theme.spacing.xs, paddingTop: theme.spacing.xs },
  observationValue: { color: theme.colors.text.secondary, fontSize: theme.fontSizes.sm, lineHeight: 20 },
  observationAction: { alignSelf: 'flex-start', color: theme.colors.primary.dark, fontSize: theme.fontSizes.xs, fontWeight: '600' },
  observationInput: { minHeight: 76 },
  readonlyNotice: { padding: theme.spacing.md, borderRadius: theme.borderRadius.md, backgroundColor: theme.colors.background.primary, borderWidth: 1, borderColor: theme.colors.border.light },
  readonlyNoticeText: { color: theme.colors.text.secondary, fontSize: theme.fontSizes.sm },
  footerNote: { textAlign: 'center', color: theme.colors.text.tertiary, fontSize: theme.fontSizes.xs, paddingVertical: theme.spacing.sm },
  actionBar: { maxWidth: 820, width: '100%', alignSelf: 'center', paddingHorizontal: theme.spacing.md, paddingTop: theme.spacing.sm, gap: theme.spacing.sm, borderTopWidth: 1, borderTopColor: theme.colors.border.light, backgroundColor: theme.colors.background.primary },
  primaryButton: { minHeight: 48, paddingHorizontal: theme.spacing.md, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.colors.primary.default, borderRadius: theme.borderRadius.md },
  primaryButtonText: { color: theme.colors.white, fontSize: theme.fontSizes.md, fontWeight: '700' },
  secondaryButton: { minHeight: 44, paddingHorizontal: theme.spacing.md, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.colors.background.primary, borderRadius: theme.borderRadius.md, borderWidth: 1, borderColor: theme.colors.primary.default },
  secondaryButtonText: { color: theme.colors.primary.dark, fontSize: theme.fontSizes.sm, fontWeight: '700' },
  buttonDisabled: { opacity: 0.6 },
  pendingStatus: { textAlign: 'center', color: '#92400E', fontSize: theme.fontSizes.xs },
  modalScreen: { flex: 1, backgroundColor: theme.colors.background.primary, padding: theme.spacing.md, gap: theme.spacing.md },
  markHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: theme.spacing.sm },
  modalTitle: { color: theme.colors.text.primary, fontSize: theme.fontSizes.lg, fontWeight: '700' },
  cancelText: { color: theme.colors.primary.dark, fontWeight: '600' },
  confirmOverlay: { flex: 1, backgroundColor: theme.colors.overlay.semi, alignItems: 'center', justifyContent: 'center', padding: theme.spacing.lg },
  confirmCard: { width: '100%', maxWidth: 420, backgroundColor: theme.colors.background.primary, borderRadius: theme.borderRadius.lg, padding: theme.spacing.lg, gap: theme.spacing.md },
  confirmActions: { flexDirection: 'row', gap: theme.spacing.sm },
});
